import styled, { createGlobalStyle, keyframes } from "styled-components";
import { animated } from "@react-spring/web";
import { tablet } from "../../styles/breakpoints";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const QuestionnaireGlobalStyle = createGlobalStyle`
  :root {
    --font-family-default: "Dosis", sans-serif;
  }
  body {
    background: linear-gradient(180deg, #f8fafc 0%, #e2e8f0 100%);
    min-height: 100vh;
    scrollbar-gutter: stable;
  }
  * {
    font-family: var(--font-family-default);
  }
`;

export const LayoutContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

export const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  background: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  position: sticky;
  top: 0;
  z-index: 10;
`;

export const LogoWrapper = styled.div`
  display: flex;
  align-items: center;

  img {
    object-fit: contain;
  }
`;

export const ProgressBarContainer = styled.div`
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  padding: 12px 16px 0;
  box-sizing: border-box;

  @media (min-width: ${tablet}px) {
    padding: 12px 0 0;
  }
`;

export const ProgressText = styled.div`
  font-size: 14px;
  color: #64748b;
  text-align: right;
  margin-top: 6px;
`;

export const ProgressBarTrack = styled.div`
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
`;

export const ProgressBarFill = styled.div<{ progress: number }>`
  height: 100%;
  width: ${({ progress }) => progress}%;
  background: linear-gradient(90deg, #0066cc, #00a3cc);
  border-radius: 4px;
  transition: width 0.3s ease;
`;

export const MainContent = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px 16px 0;
  animation: ${fadeIn} 0.5s ease-out;

  @media (min-width: ${tablet}px) {
    padding: 12px 24px 0;
  }
`;

export const SwipeableContent = styled(animated.div)`
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  touch-action: pan-y;
`;

export const ContentCard = styled.div`
  width: 100%;
  max-width: 600px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  padding: 32px;
  flex: 1;
  display: flex;
  flex-direction: column;

  @media (min-width: ${tablet}px) {
    padding: 32px 40px;
  }
`;

export const Footer = styled.footer`
  text-align: center;
  padding: 16px;
  color: #6b6b76;
  font-size: 13px;
`;

export const FixedNavBar = styled.div`
  position: sticky;
  bottom: 0;
  display: flex;
  justify-content: center;
  padding: 16px 0 calc(16px + env(safe-area-inset-bottom, 0px)) 0;
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  z-index: 5;
`;

export const NavRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  max-width: 600px;
  gap: 12px;
`;

export const NavButton = styled.button<{ variant?: "primary" | "secondary" }>`
  flex: 1;
  padding: 14px 32px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  min-height: 52px;

  ${({ variant }) =>
    variant === "secondary"
      ? `
    background: white;
    color: #0066cc;
    border: 2px solid #0066cc;
    &:hover { background: #f0f9ff; }
  `
      : `
    background: linear-gradient(135deg, #0066cc 0%, #0052a3 100%);
    color: white;
    &:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0, 102, 204, 0.4); }
    &:disabled { opacity: 0.5; cursor: not-allowed; transform: none; box-shadow: none; }
  `}
`;

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

export const RestartLink = styled.button`
  background: none;
  border: none;
  font-family: var(--font-family-default);
  font-size: 13px;
  font-weight: 600;
  color: #6b6b76;
  cursor: pointer;
  padding: 4px 0;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    color: #4a4d5e;
  }
`;

export const LanguagePickerWrapper = styled.div`
  display: flex;
  align-items: center;
`;

export const QuestionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #1a202c;
  margin: 0 0 8px;
  line-height: 1.4;
`;

export const QuestionLead = styled.p`
  font-size: 1rem;
  color: #4a5568;
  margin: 0 0 16px;
  line-height: 1.6;
  white-space: pre-line;
`;

export const QuestionDescription = styled.p`
  font-size: 1.5rem;
  color: #1a202c;
  margin: 0 0 24px;
  line-height: 1.4;
`;

export const QuestionHelpText = styled.p`
  font-size: 14px;
  color: #6b6b76;
  margin: 0 0 16px;
  font-style: italic;
`;

export const ErrorContainer = styled.div`
  width: 100%;
  max-width: 600px;
  text-align: center;
  padding: 48px 24px;

  h2 {
    font-size: 24px;
    font-weight: 600;
    color: #4a4d5e;
    margin-bottom: 12px;
  }

  p {
    font-size: 16px;
    color: #6b6b76;
    margin-bottom: 24px;
  }
`;

export const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  color: #6b6b76;
  font-size: 18px;
  gap: 16px;
`;

export const RequiredMark = styled.span`
  color: #dc2626;
  font-weight: 600;
`;

export const QuestionWrapper = styled.div<{ $hasError: boolean }>`
  border-left: ${({ $hasError }) =>
    $hasError ? "3px solid #dc2626" : "3px solid transparent"};
  padding-left: ${({ $hasError }) => ($hasError ? "12px" : "0")};
  background: ${({ $hasError }) =>
    $hasError ? "rgba(220, 38, 38, 0.03)" : "transparent"};
  border-radius: ${({ $hasError }) => ($hasError ? "4px" : "0")};
  transition: all 0.2s ease;
`;

export const ErrorMessage = styled.p`
  font-size: 13px;
  color: #dc2626;
  margin: 6px 0 0;
  font-weight: 500;
`;

export const PageGroupHeader = styled.div`
  margin-bottom: 24px;

  h3 {
    font-size: 22px;
    font-weight: 600;
    color: #4a4d5e;
    margin: 0 0 8px;
  }

  p {
    font-size: 15px;
    color: #6b6b76;
    margin: 0;
  }
`;

export const SwipeHint = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #94a3b8;
  font-size: 14px;
  margin-top: auto;
  padding-top: 24px;

  svg {
    width: 20px;
    height: 20px;
  }
`;
